export interface PageFriendlyMapControls {
  readonly coarsePointer: boolean;
  setIntentionalTouchDrag(enabled: boolean): void;
  destroy(): void;
}

interface PageFriendlyMapOptions {
  intentionalTouchDrag?: boolean;
}

/**
 * Keeps an embedded Leaflet map from trapping normal page scrolling.
 *
 * Desktop wheel/trackpad input scrolls the page; map zoom remains available
 * through the +/- controls. On coarse-pointer devices, one-finger swipes scroll
 * the page while Leaflet's two-finger touch zoom remains enabled. A screen that
 * has an explicit "Move map" control can opt back into one-finger map dragging.
 */
export function makeMapPageFriendly(
  map: any,
  element: HTMLElement,
  options: PageFriendlyMapOptions = {}
): PageFriendlyMapControls {
  const coarsePointer = window.matchMedia?.('(pointer: coarse)').matches === true
    || window.innerWidth <= 760;
  let intentionalTouchDrag = options.intentionalTouchDrag === true;

  map.scrollWheelZoom?.disable?.();
  map.touchZoom?.enable?.();

  const apply = (): void => {
    element.classList.add('page-friendly-map');
    element.classList.toggle('page-friendly-map--coarse', coarsePointer);
    element.classList.toggle(
      'page-friendly-map--intentional-drag',
      coarsePointer && intentionalTouchDrag
    );

    if (!coarsePointer || intentionalTouchDrag) map.dragging?.enable?.();
    else map.dragging?.disable?.();
  };

  apply();

  return {
    coarsePointer,
    setIntentionalTouchDrag(enabled: boolean) {
      intentionalTouchDrag = enabled;
      apply();
    },
    destroy() {
      element.classList.remove(
        'page-friendly-map',
        'page-friendly-map--coarse',
        'page-friendly-map--intentional-drag'
      );
    }
  };
}
